/**
 * Channel-specific schemas for the "example" step type.
 *
 * Each channel (SMS, Email, etc.) has its own content structure
 * that defines what fields are needed for that channel.
 *
 * All channels support a "strict" mode:
 * - strict=true: Content sent exactly as written (variables still populated)
 * - strict=false: Content used as LLM instructions/guidance
 */
import { cropConfigV2Schema } from "@redotech/redo-content-builder-common/crop-config-v2";
import { ImageType } from "@redotech/redo-content-builder-common/image-type";
import { Provider } from "@redotech/redo-order-common/provider";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  FlowContentNodeSchema,
  type FlowContentNode,
} from "../markdown-flow/nodes/flow-content-node-schema";

// =============================================================================
// CHANNEL TYPES
// =============================================================================

/**
 * Supported channel types for example content.
 * New channels can be added here as they are implemented.
 */
export const exampleChannels = [
  "sms",
  "email",
  "instagram",
  "whatsapp",
] as const;
export type ExampleChannel = (typeof exampleChannels)[number];

/**
 * Type guard for example channels.
 */
export function isExampleChannel(value: string): value is ExampleChannel {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return exampleChannels.includes(value as ExampleChannel);
}

// =============================================================================
// SMS CHANNEL
// =============================================================================

/**
 * Image attachment for SMS example content.
 */
const smsExampleImageAttachmentSchema_ = z.object({
  type: z.union([z.literal("image"), z.literal("product_image")]),
  imageSrc: z.string().nullish(),
  cropConfig: cropConfigV2Schema.optional(),
  productSelectionType: z.enum(["dynamic", "static"]),
  manuallySelectedProduct: z
    .object({ productId: z.string(), variantId: z.string() })
    .nullable()
    .optional(),
  productFilterId: zExt.objectId().nullable().optional(),
  provider: createEnum(Provider).nullish(),
  imageSourceType: createEnum(ImageType).optional(),
});

export const smsExampleImageAttachmentSchema: z.ZodType<SmsExampleImageAttachment> =
  smsExampleImageAttachmentSchema_;
export interface SmsExampleImageAttachment extends z.infer<
  typeof smsExampleImageAttachmentSchema_
> {}

/**
 * Individual SMS example item.
 * Each example has its own ID and body content.
 */
const SmsExampleItemSchema_ = z.object({
  id: z.string(),
  body: z.array(FlowContentNodeSchema),
});

export const SmsExampleItemSchema: z.ZodType<SmsExampleItem> =
  SmsExampleItemSchema_;
export interface SmsExampleItem {
  id: string;
  body: FlowContentNode[];
}

/**
 * SMS channel content for example steps.
 *
 * - examples: Array of example items (each with id and body content)
 * - imageAttachment: Optional image attachment (shared across all examples)
 * - contactCard: Whether to include brand contact card
 * - strict: When true, content sent directly; when false, used as AI guidance
 *
 * Note: Both imageAttachment and contactCard can be set simultaneously.
 *
 * Behavior based on strict mode and example count:
 * - strict + 1 example: Send directly as template
 * - strict + N examples: AI selects best example to send
 * - non-strict: All examples provided as AI guidance
 */
const SmsExampleContentSchema_ = z.object({
  examples: z.array(SmsExampleItemSchema),
  imageAttachment: smsExampleImageAttachmentSchema.optional(),
  contactCard: z.boolean().optional(),
  strict: z.boolean(),
});

export const SmsExampleContentSchema: z.ZodType<SmsExampleContent> =
  SmsExampleContentSchema_;
export interface SmsExampleContent {
  examples: SmsExampleItem[];
  /** Optional image attachment (shared across all examples) */
  imageAttachment?: SmsExampleImageAttachment;
  /** Whether to include brand contact card */
  contactCard?: boolean;
  strict: boolean;
}

// =============================================================================
// EMAIL CHANNEL (Future)
// =============================================================================

/**
 * Email channel content for example steps (not yet implemented).
 */
const EmailExampleContentSchema_ = z.object({
  subject: z.array(FlowContentNodeSchema),
  body: z.array(FlowContentNodeSchema),
  attachment: z.unknown().optional(),
  strict: z.boolean(),
});

export const EmailExampleContentSchema: z.ZodType<EmailExampleContent> =
  EmailExampleContentSchema_;
export interface EmailExampleContent extends z.infer<
  typeof EmailExampleContentSchema_
> {}

// =============================================================================
// CHANNEL CONTENT UNION
// =============================================================================

/**
 * Map of channel type to content schema.
 * Used for validation and type narrowing.
 */
export const exampleChannelContentSchemas = {
  sms: SmsExampleContentSchema,
  email: EmailExampleContentSchema,
  // instagram and whatsapp to be added later
} as const;

/**
 * All possible channel content in a single structure.
 * Each field is optional since a procedure may only use a subset of channels.
 */
const ExampleChannelsContentSchema_ = z.object({
  sms: SmsExampleContentSchema.optional(),
  email: EmailExampleContentSchema.optional(),
  instagram: z.unknown().optional(),
  whatsapp: z.unknown().optional(),
});

export const ExampleChannelsContentSchema: z.ZodType<ExampleChannelsContent> =
  ExampleChannelsContentSchema_;
export interface ExampleChannelsContent extends z.infer<
  typeof ExampleChannelsContentSchema_
> {}

// =============================================================================
// RENDERED CONTENT (after variable population)
// =============================================================================

/**
 * Individual rendered SMS example item with variables populated.
 */
const RenderedSmsExampleItemSchema_ = z.object({
  id: z.string(),
  renderedBody: z.string(),
});

export const RenderedSmsExampleItemSchema: z.ZodType<RenderedSmsExampleItem> =
  RenderedSmsExampleItemSchema_;
export interface RenderedSmsExampleItem {
  id: string;
  renderedBody: string;
}

/**
 * Rendered SMS content with variables populated.
 */
const RenderedSmsExampleContentSchema_ = z.object({
  examples: z.array(RenderedSmsExampleItemSchema),
  imageAttachment: smsExampleImageAttachmentSchema.optional(),
  contactCard: z.boolean().optional(),
  strict: z.boolean(),
});

export const RenderedSmsExampleContentSchema: z.ZodType<RenderedSmsExampleContent> =
  RenderedSmsExampleContentSchema_;
export interface RenderedSmsExampleContent extends z.infer<
  typeof RenderedSmsExampleContentSchema_
> {}

/**
 * Rendered email content with variables populated.
 */
const RenderedEmailExampleContentSchema_ = z.object({
  subject: z.string(),
  body: z.string(),
  attachment: z.unknown().optional(),
  strict: z.boolean(),
});

export const RenderedEmailExampleContentSchema: z.ZodType<RenderedEmailExampleContent> =
  RenderedEmailExampleContentSchema_;
export interface RenderedEmailExampleContent extends z.infer<
  typeof RenderedEmailExampleContentSchema_
> {}
